export interface SummaryLine {
  label: string;
  amount: string;
  period: string;
  /** Discounts render in green and italic. */
  credit?: boolean;
}

export interface PackageSummary {
  lines: SummaryLine[];
  dueToday: string;
}

/**
 * Everything the customer has chosen so far, by id. Mirrors the flow's own
 * state so a step never has to know how another step's choice is priced.
 */
export interface PackageSelection {
  secretaryPlan?: string | null;
  registeredAddress?: string | null;
  accountingPlan?: string | null;
  auditPlan?: string | null;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  /**
   * Pre-discount price, shown struck through and billed as the line item.
   * Omitted for plans the design prices at a single figure (audit & tax).
   */
  listPrice?: number;
  /** What the customer actually pays. */
  price: number;
  period: string;
  /**
   * Literal badge text for plans with no list price to derive it from. Purely
   * a label — it does not affect the total.
   */
  badgeLabel?: string;
}

export const PLANS: Plan[] = [
  {
    id: "standard",
    name: "Standard",
    description: "For companies with a stable structure",
    listPrice: 999,
    price: 699,
    period: "/year",
  },
  {
    id: "premium",
    name: "Premium",
    description: "For companies expecting structural changes",
    listPrice: 3800,
    price: 3499,
    period: "/year",
  },
];

export interface AddressOption {
  id: string;
  label: string;
  /** Condensed form for the review page's dropdown. */
  shortLabel: string;
  /** Annual cost; 0 when the customer supplies their own address. */
  price: number;
  priceLabel: string;
  /** The design fills the free option's price chip green. */
  chipGreen: boolean;
}

export const ADDRESS_OPTIONS: AddressOption[] = [
  {
    id: "service",
    label: "Yes, I want a compliant Central Hong Kong address with digital mail access",
    shortLabel: "MuteTaxes Central HK address",
    price: 2000,
    priceLabel: "HK$2,000/ year",
    chipGreen: false,
  },
  {
    id: "own",
    label: "No, I’ll use my own address and manage my own mail",
    shortLabel: "My own address",
    price: 0,
    priceLabel: "Free",
    chipGreen: true,
  },
];

export interface AccountingPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  period: string;
  featuresTitle: string;
  features: string[];
}

// NOTE: the "Included (Advanced)" lists on Accounting & Payroll and Accounting
// Premium are identical in the export, and Starter's list is the same
// PayPal/Airwallex/Stripe set used on the Company Secretary screen. Reproduced
// as given — but they read like placeholder content rather than accounting
// deliverables.
const ADVANCED_FEATURES = [
  "Change of company name (government fee excluded)",
  "Allotment of Shares",
  "Transfer of Shares",
  "Change of director (appointment/cessation)",
];

export const ACCOUNTING_PLANS: AccountingPlan[] = [
  {
    id: "starter-compliance",
    name: "Starter Compliance",
    description: "For companies with a stable structure",
    price: 3500,
    period: "/year",
    featuresTitle: "What's included",
    features: [
      "HK Company Registration",
      "PayPal with VIP Support (6% dispute tolerance)",
        "Payoneer VIP Account and 5+ more banks",
      "Airwallex Account",
    
      "Access to 9+ PayPal Reps",
      "Unlimited PayPal Reactivations",
      "Stripe",
    ],
  },
  {
    id: "accounting-payroll",
    name: "Accounting & Payroll",
    description: "For companies expecting structural changes",
    price: 5880,
    period: "/year",
    featuresTitle: "Included (Advanced)",
    features: ADVANCED_FEATURES,
  },
  {
    id: "accounting-premium",
    name: "Accounting Premium",
    description: "For companies expecting structural changes",
    price: 15120,
    period: "/year",
    featuresTitle: "Included (Advanced)",
    features: ADVANCED_FEATURES,
  },
];

/**
 * Audit & tax tiers. The design shows a single price per card plus a
 * "HK$301 OFF" badge, but no list price — and its own summary total takes
 * nothing off for it (1545 + 79 + 2350 + 3800 + 3500 + 8000 - 301 = 18,973,
 * where the 301 is the Company Secretary discount). So the badge is treated as
 * a label only. Worth confirming: if a real saving is intended, these need a
 * listPrice instead.
 */
export const AUDIT_PLANS: Plan[] = [
  {
    id: "standard",
    name: "Standard",
    description: "For companies with a stable structure",
    price: 5500,
    period: "/year",
    badgeLabel: "HK$301 OFF",
  },
  {
    id: "premium",
    name: "Premium",
    description: "For companies expecting structural changes",
    price: 8000,
    period: "/year",
    badgeLabel: "HK$301 OFF",
  },
];

/**
 * The same three tiers as the register branch — identical ids, prices and
 * feature lists — except the manage design renames the first one: an existing
 * company is buying annual filing and bookkeeping, not "starter compliance".
 * Ids are untouched, so anything that prices a selection works against either
 * list.
 */
export const MANAGE_ACCOUNTING_PLANS: AccountingPlan[] = ACCOUNTING_PLANS.map((plan) =>
  plan.id === "starter-compliance" ? { ...plan, name: "Annual Filing & Bookkeeping" } : plan,
);

/** Charged once per past financial year being brought back into compliance. */
export const REMEDIATION_PRICE_PER_YEAR = 1300;

export interface RemediationOption {
  id: string;
  label: string;
  /** Past financial years to file for, each billed at the rate above. */
  years: number;
  /**
   * True for the open-ended option, which bills at the fewest years it can
   * mean. See the note on buildManageSummary.
   */
  openEnded?: boolean;
}

export const REMEDIATION_OPTIONS: RemediationOption[] = [
  // The export reads "1 years" and "More than 3 year"; corrected here.
  { id: "1", label: "1 year", years: 1 },
  { id: "2", label: "2 years", years: 2 },
  { id: "3", label: "3 years", years: 3 },
  { id: "more-than-3", label: "More than 3 years", years: 4, openEnded: true },
];

/**
 * The accounting and audit screens word the same four options in financial
 * years rather than plain years.
 */
export const FINANCIAL_YEAR_OPTIONS: RemediationOption[] = [
  // The export reads "1 financial years"; corrected here.
  { id: "1", label: "1 financial year", years: 1 },
  { id: "2", label: "2 financial years", years: 2 },
  { id: "3", label: "3 financial years", years: 3 },
  { id: "more-than-3", label: "More than 3 financial years", years: 4, openEnded: true },
];

/**
 * A remediation service: one fixed rate rather than tiers, so it isn't a
 * `Plan` — the card shows the per-year price and the customer's chosen year
 * count multiplies it.
 */
export interface RemediationPlan {
  name: string;
  /** Omitted by the audit card, which the design shows without one. */
  description?: string;
  price: number;
  period: string;
  /** Qualifies the price, e.g. what one financial year covers. */
  periodNote: string;
  features: string[];
}

export const REMEDIATION_PLAN: RemediationPlan = {
  name: "Company Secretary Remediation",
  description: "For companies with a stable structure",
  price: REMEDIATION_PRICE_PER_YEAR,
  period: "/one time",
  periodNote: "For each past financial year",
  features: [
    "Filing of Annual Return (filing fee included)",
    "Change in director’s particulars",
  ],
};

/**
 * Bringing past financial years' books up to date.
 *
 * NOTE: the design contradicts itself on this price. The card states
 * "HK$1,300 /one time — Up to 100 transactions for each past financial year",
 * but its summary row bills "HK$3,500 / financial year" — the same amount and
 * the same (double-spaced) period as the Accounting Plan row directly above it,
 * which is what a duplicated row looks like. The card's figure is used here so
 * that what the customer is shown and what they are charged agree; that makes
 * the design's own total 9,599 rather than the 11,799 it prints. See the reply.
 */
export const ACCOUNTING_REMEDIATION_PRICE_PER_YEAR = 1300;

/** Charged per transaction beyond the card's 100-per-year allowance. */
export const ACCOUNTING_REMEDIATION_EXTRA_TRANSACTION_PRICE = 8;

export const ACCOUNTING_REMEDIATION_PLAN: RemediationPlan = {
  name: "Accounting Remediation",
  // Copied from the tier cards in the export, where it describes a plan rather
  // than a remediation. Reproduced as given.
  description: "For companies with a stable structure",
  price: ACCOUNTING_REMEDIATION_PRICE_PER_YEAR,
  period: "/one time",
  periodNote: "Up to 100 transactions for each past financial year",
  features: [
    "Review meeting with tax specialist",
    "Annual management accounts",
    "Employer’s return filing",
  ],
};

/** Auditing past financial years. Same per-year, per-100-transaction basis. */
export const AUDIT_REMEDIATION_PRICE_PER_YEAR = 5280;

export const AUDIT_REMEDIATION_PLAN: RemediationPlan = {
  name: "Audit Remediation",
  // No description in the design — unlike the other two remediation cards,
  // which both carry the tier cards' "For companies with a stable structure".
  price: AUDIT_REMEDIATION_PRICE_PER_YEAR,
  period: "/one time",
  periodNote: "Up to 100 transactions for each past financial year",
  features: ["Audited financial statement", "Up to 100 transactions for each past financial year"],
};

/**
 * One-off charges that apply regardless of which plan is chosen. Government and
 * service fees, so they are not user-adjustable on the review page.
 */
const FIXED_FEES = [
  { key: "incorporation-fee", label: "Incorporation Fee to Company Registry", amount: 1545 },
  { key: "service-fee", label: "MuteTaxes Service Fee - Incorporation", amount: 79 },
  { key: "br-fee", label: "Business Registration fee to HKSAR - Incorporation", amount: 2350 },
];

export function formatHKD(amount: number): string {
  return `HK$${amount.toLocaleString("en-US")}`;
}

export function planDiscount(plan: Plan): number {
  return plan.listPrice ? plan.listPrice - plan.price : 0;
}

/**
 * Builds the order summary for the chosen plan.
 *
 * Mirrors how the design arrives at its HK$7,473: the plan is billed at its
 * list price and the saving is broken out as its own line, so
 * 1545 + 79 + 2350 + 3800 - 301 = 7473 for Premium.
 *
 * Until a plan is picked, the plan-dependent lines are left out rather than
 * defaulting to one — showing a total for a plan the customer hasn't chosen
 * would misstate what they owe.
 */
function buildSummary({
  plan = null,
  address = null,
  accounting = null,
  audit = null,
}: {
  plan?: Plan | null;
  address?: AddressOption | null;
  accounting?: AccountingPlan | null;
  audit?: Plan | null;
} = {}): PackageSummary {
  const lines: SummaryLine[] = FIXED_FEES.map((fee) => ({
    label: fee.label,
    amount: formatHKD(fee.amount),
    period: "one time",
  }));

  if (plan) {
    lines.push({
      label: "Company Secretary",
      // Billed at list price with the saving broken out below; plans without a
      // list price bill at their single figure.
      amount: formatHKD(plan.listPrice ?? plan.price),
      period: "/ year",
    });

    const discount = planDiscount(plan);
    if (discount > 0) {
      lines.push({
        label: "Discount",
        amount: `-${formatHKD(discount)}`,
        period: "one time",
        credit: true,
      });
    }
  }

  // Only billed when the customer takes the address service; their own address
  // adds no line at all rather than a HK$0 row.
  if (address && address.price > 0) {
    lines.push({
      label: "Registered Address",
      amount: formatHKD(address.price),
      period: "/ year",
    });
  }

  if (accounting) {
    lines.push({
      label: "Accounting Plan",
      amount: formatHKD(accounting.price),
      period: "/ year",
    });
  }

  if (audit) {
    lines.push({
      label: `Audit & Tax Plan - ${audit.name}`,
      amount: formatHKD(audit.price),
      period: "/ year",
    });
  }

  const fixedTotal = FIXED_FEES.reduce((sum, fee) => sum + fee.amount, 0);

  return {
    lines,
    dueToday: formatHKD(
      fixedTotal +
        (plan?.price ?? 0) +
        (address?.price ?? 0) +
        (accounting?.price ?? 0) +
        (audit?.price ?? 0),
    ),
  };
}

/**
 * The single entry point for pricing the package.
 *
 * Resolves ids to their priced items here rather than in each step, so every
 * screen shows the same running total and choices carry both forward and
 * backward — a step that only knew its own selection would silently drop the
 * others from the total when the customer navigated back to it.
 */
export function buildPackageSummary(selection: PackageSelection): PackageSummary {
  return buildSummary({
    plan: PLANS.find((p) => p.id === selection.secretaryPlan) ?? null,
    address: ADDRESS_OPTIONS.find((a) => a.id === selection.registeredAddress) ?? null,
    accounting: ACCOUNTING_PLANS.find((a) => a.id === selection.accountingPlan) ?? null,
    audit: AUDIT_PLANS.find((a) => a.id === selection.auditPlan) ?? null,
  });
}

/**
 * "1 past year", "2 past years", "4+ past financial years" — the open-ended
 * option gets a "+" so its figure doesn't read as final.
 */
function remediationYearsLabel(option: RemediationOption, unit: string): string {
  return `${option.years}${option.openEnded ? "+" : ""} ${unit}${option.years === 1 ? "" : "s"}`;
}

/**
 * The "Manage existing business" branch prices on a different basis, per its
 * own designs:
 *
 * - No FIXED_FEES. Those are incorporation charges (Companies Registry, BR
 *   fee, our incorporation service) and the company already exists.
 * - The plan is billed at its net price on a single line, rather than at list
 *   price with the saving broken out — so no Discount row either.
 *
 * Remediation for past years is added only when they asked for it, at the
 * per-year rate times the years chosen — so the design's 3,499 + 1,300 = 4,799
 * for one past year.
 *
 * NOTE: "More than 3 years" has no price in the design. It bills here at four
 * years, the fewest that option can mean, and its summary line reads "4+ past
 * years" so the figure isn't presented as final. If it should instead be quoted
 * after review, this needs a different treatment — the total can't be honest
 * about an unbounded count.
 */
export function buildManageSummary(selection: {
  secretaryPlan?: string | null;
  pastAnnualReturns?: string | null;
  remediationYears?: string | null;
  accountingPlan?: string | null;
  pastPayrollReturns?: string | null;
  accountingRemediationYears?: string | null;
  auditPlan?: string | null;
  pastAuditStatements?: string | null;
  auditRemediationYears?: string | null;
  registeredAddress?: string | null;
}): PackageSummary {
  const plan = PLANS.find((p) => p.id === selection.secretaryPlan) ?? null;
  const accounting = ACCOUNTING_PLANS.find((a) => a.id === selection.accountingPlan) ?? null;
  const audit = AUDIT_PLANS.find((a) => a.id === selection.auditPlan) ?? null;
  // Only when they said yes: a year count left over from a previous pass
  // through the step must not keep billing after they change their mind.
  const remediation =
    selection.pastAnnualReturns === "yes"
      ? (REMEDIATION_OPTIONS.find((o) => o.id === selection.remediationYears) ?? null)
      : null;
  const accountingRemediation =
    selection.pastPayrollReturns === "yes"
      ? (FINANCIAL_YEAR_OPTIONS.find((o) => o.id === selection.accountingRemediationYears) ?? null)
      : null;
  const auditRemediation =
    selection.pastAuditStatements === "yes"
      ? (FINANCIAL_YEAR_OPTIONS.find((o) => o.id === selection.auditRemediationYears) ?? null)
      : null;
  const address = ADDRESS_OPTIONS.find((a) => a.id === selection.registeredAddress) ?? null;

  const lines: SummaryLine[] = [];

  if (plan) {
    lines.push({
      label: "Company Secretary",
      amount: formatHKD(plan.price),
      period: "/ year",
    });
  }

  const remediationTotal = remediation ? remediation.years * REMEDIATION_PRICE_PER_YEAR : 0;

  if (remediation) {
    lines.push({
      label: `Company secretary remediation - ${remediationYearsLabel(remediation, "past year")}`,
      amount: formatHKD(remediationTotal),
      period: "/ one time",
    });
  }

  if (accounting) {
    lines.push({
      label: "Accounting Plan",
      amount: formatHKD(accounting.price),
      // The manage design bills accounting per financial year, where the
      // register branch's summary just says "/ year".
      period: "/ financial year",
    });
  }

  const accountingRemediationTotal = accountingRemediation
    ? accountingRemediation.years * ACCOUNTING_REMEDIATION_PRICE_PER_YEAR
    : 0;

  if (accountingRemediation) {
    lines.push({
      label: `Accounting remediation - ${remediationYearsLabel(
        accountingRemediation,
        "past financial year",
      )}`,
      amount: formatHKD(accountingRemediationTotal),
      period: "/ one time",
    });
  }

  if (audit) {
    // Same label as the register branch. The manage design's sidebar for this
    // screen is the register one, copied wholesale — incorporation fees, list
    // price and discount row included — so it says nothing about how this
    // branch words or prices the audit line.
    lines.push({
      label: `Audit & Tax Plan - ${audit.name}`,
      amount: formatHKD(audit.price),
      period: "/ year",
    });
  }

  const auditRemediationTotal = auditRemediation
    ? auditRemediation.years * AUDIT_REMEDIATION_PRICE_PER_YEAR
    : 0;

  if (auditRemediation) {
    lines.push({
      label: `Audit remediation - ${remediationYearsLabel(
        auditRemediation,
        "past financial year",
      )}`,
      amount: formatHKD(auditRemediationTotal),
      period: "/ one time",
    });
  }

  // Only billed when they take the address service; their own address adds no
  // line at all rather than a HK$0 row, as on the register branch.
  if (address && address.price > 0) {
    lines.push({
      label: "Registered Address",
      amount: formatHKD(address.price),
      period: "/ year",
    });
  }

  return {
    lines,
    dueToday: formatHKD(
      (plan?.price ?? 0) +
        remediationTotal +
        (accounting?.price ?? 0) +
        accountingRemediationTotal +
        (audit?.price ?? 0) +
        auditRemediationTotal +
        (address?.price ?? 0),
    ),
  };
}

/**
 * Sentinel for "leave this out of the package". Radix Select rejects an empty
 * string as an item value, so optional categories need a real one.
 */
export const NOT_INCLUDED = "not-included";

/** Which piece of the selection a breakdown row edits. */
export type EditableSelection =
  | "secretaryPlan"
  | "registeredAddress"
  | "accountingPlan"
  | "auditPlan";

export interface BreakdownRow {
  key: string;
  label: string;
  amount: string;
  period: string;
  /** Discounts render green and italic. */
  credit?: boolean;
  /** Absent on rows the customer can't change (government fees, the discount). */
  edit?: {
    field: EditableSelection;
    value: string;
    options: { value: string; label: string }[];
  };
}

function optionLabel(name: string, price: number, period: string): string {
  return price > 0 ? `${name} — ${formatHKD(price)}${period}` : `${name} — Free`;
}

/**
 * The review page's editable breakdown.
 *
 * Differs from buildPackageSummary in one important way: it emits a row for
 * every adjustable category *even when nothing is chosen for it*, so a plan the
 * customer skipped earlier still has somewhere to be added from. Company
 * Secretary and a registered address are legally required in Hong Kong, so
 * those offer no "not included" choice — only cheaper alternatives.
 *
 * The total comes from the same buildSummary as every other step, so an edit
 * here can't put the breakdown and the quote out of step.
 */
export function buildPackageBreakdown(selection: PackageSelection): {
  rows: BreakdownRow[];
  dueToday: string;
} {
  const plan = PLANS.find((p) => p.id === selection.secretaryPlan) ?? null;
  const address = ADDRESS_OPTIONS.find((a) => a.id === selection.registeredAddress) ?? null;
  const accounting = ACCOUNTING_PLANS.find((a) => a.id === selection.accountingPlan) ?? null;
  const audit = AUDIT_PLANS.find((a) => a.id === selection.auditPlan) ?? null;

  const rows: BreakdownRow[] = FIXED_FEES.map((fee) => ({
    key: fee.key,
    label: fee.label,
    amount: formatHKD(fee.amount),
    period: "one time",
  }));

  rows.push({
    key: "secretary",
    label: "Company Secretary",
    amount: plan ? formatHKD(plan.listPrice ?? plan.price) : "—",
    period: plan ? "/ year" : "",
    edit: {
      field: "secretaryPlan",
      value: plan?.id ?? NOT_INCLUDED,
      options: PLANS.map((p) => ({
        value: p.id,
        label: optionLabel(p.name, p.price, p.period),
      })),
    },
  });

  const discount = plan ? planDiscount(plan) : 0;
  if (discount > 0) {
    rows.push({
      key: "discount",
      label: "Discount",
      amount: `-${formatHKD(discount)}`,
      period: "one time",
      credit: true,
    });
  }

  rows.push({
    key: "address",
    label: "Registered Address",
    amount: address ? (address.price > 0 ? formatHKD(address.price) : "Free") : "—",
    period: address && address.price > 0 ? "/ year" : "",
    edit: {
      field: "registeredAddress",
      value: address?.id ?? NOT_INCLUDED,
      options: ADDRESS_OPTIONS.map((a) => ({
        value: a.id,
        label: optionLabel(a.shortLabel, a.price, " / year"),
      })),
    },
  });

  rows.push({
    key: "accounting",
    label: "Accounting Plan",
    amount: accounting ? formatHKD(accounting.price) : "Not included",
    period: accounting ? "/ year" : "",
    edit: {
      field: "accountingPlan",
      value: accounting?.id ?? NOT_INCLUDED,
      options: [
        ...ACCOUNTING_PLANS.map((a) => ({
          value: a.id,
          label: optionLabel(a.name, a.price, a.period),
        })),
        { value: NOT_INCLUDED, label: "Not included" },
      ],
    },
  });

  rows.push({
    key: "audit",
    label: audit ? `Audit & Tax Plan - ${audit.name}` : "Audit & Tax Plan",
    amount: audit ? formatHKD(audit.price) : "Not included",
    period: audit ? "/ year" : "",
    edit: {
      field: "auditPlan",
      value: audit?.id ?? NOT_INCLUDED,
      options: [
        ...AUDIT_PLANS.map((a) => ({
          value: a.id,
          label: optionLabel(a.name, a.price, a.period),
        })),
        { value: NOT_INCLUDED, label: "Not included" },
      ],
    },
  });

  return { rows, dueToday: buildSummary({ plan, address, accounting, audit }).dueToday };
}
